import { PortfolioEditor } from "@/components/toolkit/portfolio-editor";
import {
  canEditPortfolio,
  effectiveOrgRole,
  toolkitSearchSchema,
  useToolkitProfile,
} from "@/components/toolkit/use-toolkit-profile";
import { Skeleton } from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/(org)/o/$orgSlug/toolkit")({
  validateSearch: toolkitSearchSchema,
  component: ToolkitPage,
});

function ToolkitPage() {
  const { orgSlug } = Route.useParams();
  const { profile: profileSlugParam } = Route.useSearch();
  // Preview is limited to owners/admins (the preview route's gate); hide the
  // button for anyone else rather than open a not-found tab.
  const canPreview = canEditPortfolio(
    effectiveOrgRole(Route.useRouteContext()),
  );

  const {
    organizationId,
    profiles,
    target,
    profileNotFound,
    isPending,
    previewData,
    previewPending,
    previewError,
  } = useToolkitProfile(orgSlug, profileSlugParam);

  if (isPending) {
    return (
      <div className="flex flex-col gap-5 px-6 py-7 md:px-9">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[60svh] w-full rounded-2xl" />
      </div>
    );
  }

  if (profileNotFound) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="text-xl font-semibold">Profile not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This organization has no profile with that link.
        </p>
        <Link
          to="/o/$orgSlug/toolkit"
          params={{ orgSlug }}
          className="mt-4 inline-block text-sm font-medium underline underline-offset-4"
        >
          Open your portfolio
        </Link>
      </div>
    );
  }

  if (!target) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="text-xl font-semibold">No profile yet</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Create a creator profile to start building your portfolio.
        </p>
      </div>
    );
  }

  return (
    <PortfolioEditor
      // Remount when the edited profile changes so editor state (theme, save
      // baseline) never carries across profiles.
      key={target.id}
      organizationId={organizationId}
      orgSlug={orgSlug}
      profile={{
        id: target.id,
        slug: target.slug,
        metadata: target.metadata,
        email: target.email,
      }}
      profiles={profiles?.map((p) => ({
        slug: p.slug,
        name: p.name,
        avatarUrl: p.avatarUrl,
      }))}
      previewData={previewData}
      previewPending={previewPending}
      previewError={previewError}
      canPreview={canPreview}
    />
  );
}
